import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  OnInit,
  Output,
} from '@angular/core';
import { StepCarouseldModule } from '@celerofinancas/ui-common';
import { ModalRef } from '@celerofinancas/ui-modals';
import { BehaviorSubject, finalize, map, switchMap, takeUntil } from 'rxjs';
import {
  OnboardingCarouselModal,
  STEP_INITIAL_JOURNEY,
  STEP_BY_STEP,
} from './step-by-step-initial-journey.const';
import { AsyncPipe, CommonModule } from '@angular/common';
import { ModalBaseComponent } from '@shared/components/modal-base/modal-base.component';
import { CheckFirstAccessService } from '@shared/services/check-first-access/check-first-access.service';
import { Store } from '@ngxs/store';
import { PatchCurrentUserAction } from '@celerofinancas/state-management';
import { PrivacyPolicy, PrivacyPolicyService } from '@celerofinancas/ui-http-services';
import { DestroyController } from '@celerofinancas/class-utils';
import { CdsSnackbarService } from '@celerofinancas/design-system';
import { UiDirectivesModule } from '@celerofinancas/ui-directives';
import { LINKS } from '../../configs/environments-ref';
import { ShimmerComponent } from '@shared/components/shimmer/shimmer.component';

/**
 * Interface for the event emitted by the step carousel.
 */
interface StepCarouselEvent {
  previous: { id: string; checked: boolean };
  current: { id: string; checked: boolean };
}

/**
 * Component for the initial journey modal.
 */
@Component({
  selector: 'caixa-webcomponent-initial-journey-modal',
  templateUrl: './initial-journey-modal.component.html',
  standalone: true,
  imports: [
    AsyncPipe,
    StepCarouseldModule,
    CommonModule,
    UiDirectivesModule,
    ShimmerComponent,
  ],
  styleUrls: ['./initial-journey-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.acceptance-view]': 'isAcceptanceView',
  },
})
export class InitialJourneyModalComponent extends DestroyController implements OnInit {
  /**
   * Event emitter to signal the need to connect accounts.
   */
  @Output()
  public connectAccounts = new EventEmitter();

  /**
   * Reference to the current modal instance.
   */
  public readonly modalRef = inject(ModalRef<any, OnboardingCarouselModal>);

  /**
   * Reference to the modal base component.
   */
  public modal = inject(ModalBaseComponent);

  /**
   * Check First Access Service
   */
  public checkFirstAccess = inject(CheckFirstAccessService);

  /**
   * Change detector ref for manual change detection
   */
  private cdr = inject(ChangeDetectorRef);

  /**
   * NGXS Store
   */
  private store = inject(Store);

  /**
   * Privacy Policy Service
   */
  public terms = inject(PrivacyPolicyService);

  /**
   * Snackbar Service to show alerts
   */
  public snackbar = inject(CdsSnackbarService);

  /**
   * Behavior subject to hold the state of the initial onboarding flow.
   */
  public initialFlowState$ = new BehaviorSubject<OnboardingCarouselModal[]>(
    Object.values(STEP_BY_STEP)
  );

  /**
   * Transforms the initialFlowState$ BehaviorSubject into an array of its values
   */
  public listOptions$ = this.initialFlowState$.pipe(map((value) => Object.values(value)));

  /**
   * Check the current step is the last step
   */
  public isLastStep$ = new BehaviorSubject(false);

  /**
   * Check the current step is the first step
   */
  public isFirstStep$ = new BehaviorSubject(false);

  /**
   * Wether page is scrolled and header should be contracted
   */
  public loading$ = new BehaviorSubject(false);

  /**
   * Current privacy policy object
   */
  public privacyPolicy$ = new BehaviorSubject<PrivacyPolicy>({
    version: '',
    release_date: '',
  });

  /**
   * Flag to toggle the inner "Terms and Conditions" view of the welcome modal.
   * `false` = tela de aceite ("Acesse GRATUITAMENTE o Meu CAIXA"); `true` = full terms view.
   */
  public showTerms = false;

  /**
   * Holds the state of the flow after the user accepts the terms
   */
  public initflowAfterAcceptTerm: OnboardingCarouselModal;

  /**
   * The first step in the onboarding process
   */
  public firstStep = 'termOfAcceptance';

  /**
   * Whether the acceptance screen is on display — it uses the narrower (480px)
   * layout from the prototype. The terms sub-view and the carousel steps keep
   * the default width.
   */
  public get isAcceptanceView(): boolean {
    return this.modalRef.data?.id === this.firstStep && !this.showTerms;
  }

  /**
   * Lifecycle hook that is called after data-bound properties are initialized.
   */
  public async ngOnInit() {
    this.modalRef.beforeClose = () => {
      this.modalRef.data.checked = false;
      return true;
    };
    this.modalRef.data = this.initialFlowState$.value[STEP_INITIAL_JOURNEY.termOfAcceptance];
    await this.verifyAcceptanceTermsShow();
    this.modal.updateModalData();
  }

  /**
   * Handles the transition to the next step in the onboarding carousel.
   */
  public goToLastStep() {
    this.modalRef.data.checked = !this.modalRef.data.checked;
    const finallyStep = this.initialFlowState$.value.find((value) => value.lastStep === true);
    this.transitionToStep({
      previous: this.modalRef.data,
      current: finallyStep,
    });
  }

  /**
   * Transitions the tour to a specified step, updating the state accordingly
   * @param param0 An object containing the previous and current steps
   */
  public transitionToStep({ previous, current }: StepCarouselEvent): void {
    const flowStateArr = this.initialFlowState$.value;
    const previousFull = flowStateArr.find((step) => step.id === previous.id) ?? flowStateArr[0];
    const currentFull = flowStateArr.find((step) => step.id === current.id) ?? flowStateArr[0];

    this.isLastStep$.next(currentFull.id === 'openFinance');

    if (currentFull.id === 'openFinance') {
      flowStateArr.find((step) => step.id === 'openFinance').checked = true;
    }

    previousFull.checked = previous.checked;
    currentFull.checked = current.checked;

    this.initialFlowState$.next([...flowStateArr]);
    this.modal.headerTitle = currentFull.headerTitle;
    this.modal.showCloseModal = currentFull.showCloseModal;
    this.modalRef.data = currentFull;
  }

  /**
   * Initializes the flow state with the default steps.
   */
  public initFlow(): void {
    this.initflowAfterAcceptTerm = this.initialFlowState$.value[STEP_INITIAL_JOURNEY.welcome];

    this.removeTermOfAcceptance();

    const previous = this.modalRef.data;
    previous.checked = true;

    this.transitionToStep({ previous, current: this.initflowAfterAcceptTerm });
  }

  /**
   * Removes the terms of acceptance from the flow state.
   */
  public removeTermOfAcceptance(): void {
    const currentOptions = this.initialFlowState$.getValue();
    const filteredOptions = currentOptions.filter((option) => option.id !== this.firstStep);

    this.initialFlowState$.next(filteredOptions);

    this.listOptions$ = this.initialFlowState$.pipe(map((value) => Object.values(value)));
  }

  /**
   * Opens the inner "Terms and Conditions" view within the welcome modal,
   * swapping the header title accordingly.
   */
  public viewTerms(): void {
    this.showTerms = true;
    this.modal.headerTitle =
      this.modalRef.data?.termsHeaderTitle ?? 'TERMOS E CONDIÇÕES DE USO';
    this.modal.showCloseModal = false;
  }

  /**
   * Returns from the terms view back to the welcome view, restoring the header.
   */
  public backToWelcome(): void {
    this.showTerms = false;
    this.modal.headerTitle = this.modalRef.data?.headerTitle ?? 'Meu CAIXA';
  }

  /**
   * Closes the modal.
   */
  public closeModal(): void {
    this.modalRef.data.checked = false;
    this.modalRef.close();
  }

  /**
   * Saves the current user as having accepted the privacy policy.
   * O aceite é implícito: clicar em "Acessar Meu CAIXA" registra a concordância.
   */
  public acceptTerms(): void {
    this.loading$.next(true);
    this.terms
      .acceptPrivacyPolicy(this.privacyPolicy$.value.version)
      .pipe(
        switchMap(() =>
          this.store.dispatch(
            new PatchCurrentUserAction({
              has_accepted_latest_policy: true,
              privacy_policy_acceptance_date: new Date().toUTCString(),
            }),
          ),
        ),
        takeUntil(this.destroyController$),
        finalize(() => {
          this.loading$.next(false);
          this.initFlow();
        }),
      )
      .subscribe({
        next: () => {
          this.snackbar.alert('success', 'Termos de uso aceitos com sucesso!');
        },
        error: () => {
          this.snackbar.alert('error', 'Erro ao aceitar os termos de uso.');
        },
      });
  }

  /**
   * Connects the user's accounts with Open Finance.
   */
  public connectAccountsOpenFinance(): void {
    window.open(LINKS.open_finance, '_blank');
    this.closeModal();
  }

  /**
   * Verifies if the acceptance terms should be shown.
   */
  public async verifyAcceptanceTermsShow(): Promise<void> {
    try {
      const term = await this.checkFirstAccess.getAcceptanceTerm();
      this.initialFlowState$.value[STEP_INITIAL_JOURNEY.termOfAcceptance].body = term.value.body;
      this.privacyPolicy$.next(term.value);
      this.cdr.detectChanges();
    } catch (err) {
      console.error('Erro ao carregar termo:', err);
    }
  }
}
